import React from "react";
import { motion } from "framer-motion";
import { BookOpen, Users, Heart, HandHelping, Sparkles, Home as HomeIcon, ExternalLink } from "lucide-react";
import { PastorMessage } from "@/components/PastorMessage";
import { BrandGraphic } from "@/components/BrandGraphic";

const BELIEFS = [
  { icon: BookOpen, title: "Scripture", body: "The Bible is our foundation and authority for faith and life." },
  { icon: Heart, title: "Faith in Jesus", body: "Salvation through grace alone in Jesus Christ our Savior." },
  { icon: Users, title: "Community", body: "Doing life together as a family of believers across generations." },
  { icon: HandHelping, title: "Service", body: "Loving our neighbors and serving with compassion." },
  { icon: Sparkles, title: "Prayer", body: "A daily, vibrant connection with our Heavenly Father." },
  { icon: HomeIcon, title: "Family", body: "Building strong homes anchored in Christ." },
];

const LEADERS = [
  { role: "Pastor", name: "Abraham Binzuwah Jr." },
  { role: "Head Elder", name: "Jeremiah Adu" },
  { role: "Church Clerk", name: "Kwame Konadu" },
  { role: "Treasurer", name: "Winifred Tham" },
  { role: "Ministry Leaders", name: "Various" },
];

export default function About() {
  return (
    <div data-testid="about-page">
      {/* Hero */}
      <section className="bg-[#003B5C] py-28 lg:py-36 relative overflow-hidden grain-overlay">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 relative">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#D4A437] mb-6">About Us</div>
            <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl font-black tracking-tighter leading-[0.95] text-white max-w-4xl">
              Our Story.
            </h1>
          </motion.div>
        </div>
      </section>

      {/* Story */}
      <section className="py-24 lg:py-32 bg-white">
        <div className="max-w-4xl mx-auto px-6 sm:px-8 lg:px-12">
          <p className="text-xl sm:text-2xl leading-relaxed text-[#1E1E1E]/85 font-light">
            Garland Ebenezer Ghanaian Seventh-day Adventist Church is a Christ-centered community
            committed to <span className="text-[#003B5C] font-semibold">worship, Bible truth, fellowship, and service.</span>
          </p>
          <p className="mt-8 text-lg leading-relaxed text-[#1E1E1E]/75">
            Our church exists to help families grow in faith, strengthen one another, and share
            the hope of Jesus Christ with our community. We are a multi-generational, multi-cultural
            family rooted in the rich heritage of Ghanaian Adventism while embracing our home in Texas.
          </p>
        </div>
      </section>

      {/* Brand graphic break */}
      <section className="bg-[#F7F8FA] py-0">
        <div className="max-w-7xl mx-auto h-[280px] lg:h-[360px]">
          <BrandGraphic variant="stripes">
            <div className="absolute inset-0 flex items-center justify-center text-center px-8">
              <div>
                <div className="text-xs uppercase tracking-[0.3em] text-[#D4A437] font-bold mb-4">Established in Faith</div>
                <h3 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold text-white tracking-tight leading-tight">
                  Worship · Fellowship · Service
                </h3>
                <div className="w-14 h-[3px] bg-[#D4A437] mx-auto mt-6" />
              </div>
            </div>
          </BrandGraphic>
        </div>
      </section>

      {/* About Seventh-day Adventists */}
      <section data-testid="about-adventists-section" className="py-24 lg:py-32 bg-white">
        <div className="max-w-4xl mx-auto px-6 sm:px-8 lg:px-12">
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#D4A437] mb-5">About Seventh-day Adventists</div>
          <h2 className="font-display text-4xl sm:text-5xl font-bold tracking-tight text-[#003B5C] leading-tight mb-10">
            About Seventh-day Adventists.
          </h2>
          <div className="space-y-6 text-base sm:text-lg leading-[1.8] text-[#1E1E1E]/80">
            <p>
              The Seventh-day Adventist Church is a mainstream Protestant church with millions of
              members worldwide. The Seventh-day Adventist Church seeks to enhance the quality of
              life for people everywhere and to let people know that Jesus is coming again soon.
            </p>
            <p>
              Adventists believe in one God: Father, Son, and Holy Spirit. Salvation is made
              possible through Jesus Christ, who came to earth, lived a sinless life, died for the
              sins of the world, rose again, and now ministers for His people. We believe Jesus
              will return to complete His plan of salvation and take His people home.
            </p>
            <p>
              Adventists believe that God is concerned with the whole person. The way we live, eat,
              speak, think, treat each other, care for our families, and serve our community are
              all part of our relationship with Him.
            </p>
          </div>
          <a
            href="https://adventist.org/en/beliefs"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="about-adventists-cta"
            className="inline-flex items-center gap-2 mt-10 bg-[#003B5C] text-white px-7 py-3.5 rounded-sm font-medium hover:bg-[#002A42] transition-all group"
          >
            FIND OUT MORE
            <ExternalLink size={16} className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
          </a>
        </div>
      </section>

      {/* PASTOR MESSAGE */}
      <PastorMessage variant="muted" />

      {/* Beliefs */}
      <section className="py-24 lg:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <div className="mb-16 max-w-2xl">
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#D4A437] mb-4">Beliefs &amp; Values</div>
            <h2 className="font-display text-4xl sm:text-5xl font-bold tracking-tight text-[#003B5C] leading-tight">
              What we stand on.
            </h2>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {BELIEFS.map(({ icon: Icon, title, body }) => (
              <div key={title} data-testid={`belief-${title.toLowerCase().replace(/\s+/g, "-")}`} className="group p-8 border border-gray-200 rounded-sm hover:border-[#D4A437] hover:shadow-md transition-all">
                <div className="w-12 h-12 rounded-sm bg-[#003B5C] flex items-center justify-center mb-5 group-hover:bg-[#D4A437] transition-colors">
                  <Icon size={22} className="text-white" />
                </div>
                <h3 className="font-display text-xl font-bold text-[#003B5C] mb-2">{title}</h3>
                <p className="text-sm leading-relaxed text-[#1E1E1E]/70">{body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Leadership */}
      <section className="py-24 lg:py-32 bg-[#F7F8FA]">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <div className="mb-16 max-w-2xl">
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#D4A437] mb-4">Leadership</div>
            <h2 className="font-display text-4xl sm:text-5xl font-bold tracking-tight text-[#003B5C] leading-tight">
              Meet the team.
            </h2>
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-5 gap-5">
            {LEADERS.map((l) => (
              <div key={l.role} data-testid={`leader-${l.role.toLowerCase().replace(/\s+/g, "-")}`} className="bg-white border border-gray-100 p-6 rounded-sm hover:shadow-md transition-all">
                <div className="w-full aspect-square rounded-sm bg-gradient-to-br from-[#003B5C] to-[#002A42] mb-5 flex items-center justify-center">
                  <span className="font-display text-4xl font-black text-[#D4A437]">
                    {l.role.charAt(0)}
                  </span>
                </div>
                <div className="text-[10px] uppercase tracking-[0.18em] text-[#D4A437] font-bold mb-1">{l.role}</div>
                <div className="font-display font-bold text-[#003B5C]">{l.name}</div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
